import { z } from "zod";

export const MusicMetadata = z.object({
  moods: z.array(z.string()),
  genres: z.array(z.string()),
  tags: z.array(z.string()),
});

export const BackgroundMusicDto = z.object({
  externalId: z.string(),
  src: z.string(),
  name: z.string(),
  durationMs: z.number(),
  userAdded: z.boolean(),
  isFavorite: z.boolean().default(false),
  hasBeatSync: z.boolean().default(false),
  metadata: MusicMetadata.nullable(),
  deletedAt: z.string().nullable(),
});

export const BackgroundMusicWithPeaksDto = BackgroundMusicDto.extend({
  peaks: z.array(z.array(z.number())).nullable(),
});

export const ToggleBackgroundMusicFavoriteDto = z.object({
  isFavorite: z.boolean(),
});

export const GetBackgroundMusicsByExtIdsDto = z.object({
  externalIds: z.array(z.string()),
});

export const GetBackgroundMusicsByExtIdsResponseDto =
  BackgroundMusicWithPeaksDto.array();

export const BackgroundMusicOwnershipFilterDto = z.enum([
  "ALL",
  "USER",
  "LIBRARY",
  "BEAT_SYNC",
]);

export const GetPagedBackgroundMusicsBodyDto = z.object({
  pageSize: z.number().min(1).max(100).default(25),
  search: z.string().optional(),
  ownership: BackgroundMusicOwnershipFilterDto.optional(),
  genres: z.array(z.string()).optional(),
  moods: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  favoritesOnly: z.boolean().optional(),
  cursorIsTeamTrack: z.boolean().optional(),
  cursorName: z.string().optional(),
  cursorExternalId: z.string().optional(),
});

export const PagedBackgroundMusicsResponseDto = z.object({
  backgroundMusics: z.array(BackgroundMusicDto),
  nextCursor: z
    .object({
      isTeamTrack: z.boolean(),
      name: z.string(),
      externalId: z.string(),
    })
    .optional(),
});

export const PendingBackgroundMusicPeaksQueryDto = z.object({
  size: z.coerce.number().int().min(1).max(100).default(10),
});

export const PendingBackgroundMusicPeaksResponseDto = z.object({
  remaining: z.number(),
  tracks: z.array(
    z.object({
      externalId: z.string(),
      name: z.string(),
      src: z.string(),
      hasPeaks: z.boolean(),
    }),
  ),
});

export const SaveBackgroundMusicPeaksDto = z.object({
  externalId: z.string(),
  durationMs: z.number(),
  peaks: z.array(z.array(z.number())).optional(),
});

export const SaveBackgroundMusicPeaksResponseDto = z.object({
  remaining: z.number(),
});

export const UploadBackgroundMusicDto = z.object({
  name: z.string(),
  durationMs: z.coerce.number().optional(),
  genres: z.string().refine(
    (val) => {
      try {
        const parsed = JSON.parse(val);
        return (
          Array.isArray(parsed) &&
          parsed.every((item) => typeof item === "string")
        );
      } catch {
        return false;
      }
    },
    { message: "genres must be a JSON string array" },
  ),
  moods: z.string().refine(
    (val) => {
      try {
        const parsed = JSON.parse(val);
        return (
          Array.isArray(parsed) &&
          parsed.every((item) => typeof item === "string")
        );
      } catch {
        return false;
      }
    },
    { message: "moods must be a JSON string array" },
  ),
  tags: z.string().refine(
    (val) => {
      try {
        const parsed = JSON.parse(val);
        return (
          Array.isArray(parsed) &&
          parsed.every((item) => typeof item === "string")
        );
      } catch {
        return false;
      }
    },
    { message: "tags must be a JSON string array" },
  ),
  peaks: z
    .string()
    .refine(
      (val) => {
        try {
          const parsed = JSON.parse(val);
          return (
            Array.isArray(parsed) &&
            parsed.every(
              (channel) =>
                Array.isArray(channel) &&
                channel.every((n) => typeof n === "number"),
            )
          );
        } catch {
          return false;
        }
      },
      { message: "peaks must be a JSON number[][] array" },
    )
    .optional(),
});

export const BeatSyncDto = z.object({
  externalId: z.string(),
  trackExternalId: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  cuttingPoints: z.array(z.number()),
  bpm: z.number(),
  gridOffsetMs: z.number(),
  isGlobal: z.boolean(),
});

export const CreateBeatSyncDto = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().nullable().default(null),
  cuttingPoints: z.array(z.number()),
  bpm: z.number().default(120),
  gridOffsetMs: z.number().default(0),
  isGlobal: z.boolean().default(false),
});

export type MusicMetadata = z.infer<typeof MusicMetadata>;
export type BackgroundMusicDto = z.infer<typeof BackgroundMusicDto>;
export type BackgroundMusicWithPeaksDto = z.infer<
  typeof BackgroundMusicWithPeaksDto
>;
export type ToggleBackgroundMusicFavoriteDto = z.infer<
  typeof ToggleBackgroundMusicFavoriteDto
>;
export type GetBackgroundMusicsByExtIdsDto = z.infer<
  typeof GetBackgroundMusicsByExtIdsDto
>;
export type GetBackgroundMusicsByExtIdsResponseDto = z.infer<
  typeof GetBackgroundMusicsByExtIdsResponseDto
>;
export type BackgroundMusicOwnershipFilterDto = z.infer<
  typeof BackgroundMusicOwnershipFilterDto
>;
export type GetPagedBackgroundMusicsBodyDto = z.infer<
  typeof GetPagedBackgroundMusicsBodyDto
>;
export type PagedBackgroundMusicsResponseDto = z.infer<
  typeof PagedBackgroundMusicsResponseDto
>;
export type PendingBackgroundMusicPeaksQueryDto = z.infer<
  typeof PendingBackgroundMusicPeaksQueryDto
>;
export type PendingBackgroundMusicPeaksResponseDto = z.infer<
  typeof PendingBackgroundMusicPeaksResponseDto
>;
export type SaveBackgroundMusicPeaksDto = z.infer<
  typeof SaveBackgroundMusicPeaksDto
>;
export type SaveBackgroundMusicPeaksResponseDto = z.infer<
  typeof SaveBackgroundMusicPeaksResponseDto
>;
export type UploadBackgroundMusicDto = z.infer<typeof UploadBackgroundMusicDto>;
export type BeatSyncDto = z.infer<typeof BeatSyncDto>;
export type CreateBeatSyncDto = z.infer<typeof CreateBeatSyncDto>;
